import React from 'react';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import type { HTMLAttributes } from 'react';
import type { SystemColorType } from '../../util/types';
import { AlertWrapperStyled } from './Alert.styled';
import { ALERT_WRAPPER, ICON_WRAPPER } from '../../util/data-id';
import MaterialIcon from '../icon/Icon.material';
import Slide from '../transitions/Slide';
import { GLOBAL_ICONS } from '../../util/vars';
import ButtonIcon from '../button/Button.icon';
import styled from 'styled-components';
import { useSessionStorage } from 'react-use';
import { useIdentity } from '@whizz/react-identity';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;

interface Props extends HTMLProps {
  /**
   * Displays alert.
   */
  show: boolean;
  /**
   * Sets alert color/type.
   */
  type?: SystemColorType;
  /**
   * Sets alert headline.
   */
  headline?: React.ReactNode | string;
  /**
   * Removes type icon.
   */
  disableIcon?: boolean;

  invert?: boolean;

  width?: string;

  dismissable?: {
    id: string,
  } | false;
}

const Alert = React.forwardRef<Ref, Props>(({
  show,
  type = 'info',
  headline,
  disableIcon = true,
  invert = false,
  dismissable = false,
  width = 'w-full',
  children,
  ...other
}: Props, ref): JSX.Element => {
  const { me } = useIdentity();
  const [ dismissed, setDismissed ] = dismissable && dismissable?.id ? useSessionStorage(`${dismissable.id}-${(me as Teacher).id}`, false) : [ false, () => console.info('setDismissed') ];

  return (
    <Slide when={show && !dismissed}>
      <div {...other as HTMLProps}>
        <div className={`${width} bg-white rounded-default`}>
          <AlertWrapperStyled ref={ref} role={'alert'} $color={type} $invert={invert} data-id={ALERT_WRAPPER}>
            <div className={'container w-full m-0 flex items-start'}>
              {!disableIcon && (
                <MaterialIcon icon={GLOBAL_ICONS[type]} color={invert ? 'currentColor' : type} size={'md'} className={'mr-sm'} />
              )}
              <div>
                {headline && <div>{headline}</div>}
                {children}
              </div>
            </div>
            { dismissable ?
              <div className={'absolute top-0 right-0'}>
                <ClearIcon
                  icon={GLOBAL_ICONS.close}
                  onClick={() => setDismissed(true)}
                  type={'button'}
                  size={'md'}
                  color={'transparent'}
                  isCircle
                />
              </div> : null }
          </AlertWrapperStyled>
        </div>
      </div>
    </Slide>);
});

const ClearIcon = styled(ButtonIcon)`
  [data-id='${ICON_WRAPPER}'] {
    width: calc(${(props) => props.theme.sizes[props.size ?? 'md']}/3);
    height: calc(${(props) => props.theme.sizes[props.size ?? 'md']}/3);
  }
`;

Alert.displayName = 'Alert';

export default Alert;
